import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, ArrowUpRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { PageHero } from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const empty = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const { config } = useSiteConfig();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Bericht verzonden! We nemen snel contact op.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Zeg hallo" title={<>Contacteer<br />ons.</>} testid="contact-hero" />
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <h2 className="font-serif text-4xl md:text-5xl text-[#0B0B0C] leading-[1.05] mb-8">
            Vragen, ideeën of samenwerken? We horen graag van je.
          </h2>
          <ul className="space-y-6">
            <li className="flex gap-4">
              <span className="h-11 w-11 grid place-items-center rounded-full text-white shrink-0" style={{ background: "var(--elsa-blue)" }}><MapPin size={18} /></span>
              <div><p className="font-600 text-[#0B0B0C]">Adres</p><p className="text-muted-foreground" data-testid="contact-address">{config.contact_address}</p></div>
            </li>
            <li className="flex gap-4">
              <span className="h-11 w-11 grid place-items-center rounded-full text-white shrink-0" style={{ background: "var(--elsa-blue)" }}><Mail size={18} /></span>
              <div><p className="font-600 text-[#0B0B0C]">E-mail</p><a href={`mailto:${config.contact_email}`} className="text-muted-foreground hover:text-[#0A3087] transition-colors" data-testid="contact-email-value">{config.contact_email}</a></div>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="border border-black/10 bg-white p-8 md:p-10 space-y-5" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name">Naam *</Label>
                <Input id="name" data-testid="contact-name" required value={form.name} onChange={set("name")} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="cemail">E-mail *</Label>
                <Input id="cemail" type="email" data-testid="contact-email" required value={form.email} onChange={set("email")} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label htmlFor="subject">Onderwerp *</Label>
              <Input id="subject" data-testid="contact-subject" required value={form.subject} onChange={set("subject")} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="message">Bericht *</Label>
              <Textarea id="message" data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")} className="mt-1.5" />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="group inline-flex items-center gap-2 text-sm font-600 px-7 py-3.5 rounded-full text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              style={{ background: "var(--elsa-blue)" }}
            >
              {loading ? "Bezig..." : "Verstuur bericht"}
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
